import moment from "moment-timezone";
import { useConfigStore } from "../Stores/Config";

type DayName =
  | "Sunday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday";

const dayNameToNumber: Record<DayName, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export const isOnline = () => {
  const config = useConfigStore.getState().config;

  if (config?.alwaysOnline) {
    return {
      online: true,
      nextOpeningTime: null,
      currentOpeningTime: null,
    };
  }

  if (!config.openingTimes) {
    return {
      online: false,
      nextOpeningTime: null,
      currentOpeningTime: null,
    };
  }

  // Use the configured timezone
  const tz = config?.timezone?.trim() ?? "UTC";
  const now = moment.tz(tz);
  const nowUtc = now.clone().utc();

  // Arrays to collect schedule periods and next-occurrence candidates.
  const periods = [];
  const nextCandidates = [];

  // Loop through each scheduled rule.
  for (let i = 0; i < config.openingTimes.length; i++) {
    const schedule = config.openingTimes[i];
    let days = [];
    if (schedule.day === "Weekdays") {
      days = [1, 2, 3, 4, 5];
    } else if (schedule.day === "Weekends") {
      days = [6, 0];
    } else if (schedule.day === "All days") {
      days = [0, 1, 2, 3, 4, 5, 6];
    } else {
      days.push(dayNameToNumber[schedule.day as DayName]);
    }

    // Process each day from the rule
    for (let j = 0; j < days.length; j++) {
      const day = days[j];

      // Parse times
      const [openH, openM] = schedule.openingTime.split(":").map(Number);
      const [closeH, closeM] = schedule.closingTime.split(":").map(Number);

      // Build candidate opening and closing times in the business’s local timezone.
      let candidateOpening = moment.tz(tz).day(day).set({
        hour: openH,
        minute: openM,
        second: 0,
        millisecond: 0,
      });
      let candidateClosing = moment.tz(tz).day(day).set({
        hour: closeH,
        minute: closeM,
        second: 0,
        millisecond: 0,
      });

      // Adjust for overnight periods (e.g. 22:00–02:00)
      if (candidateClosing.isBefore(candidateOpening)) {
        candidateClosing.add(1, "day");
      }

      // Add the period as computed to our list.
      periods.push({
        opening: candidateOpening.clone().utc(),
        closing: candidateClosing.clone().utc(),
        original: schedule,
      });

      // Compute the next occurrence candidate.
      // If the candidate opening is already in the past relative to 'now' (in local tz),
      // then add 7 days to get the upcoming occurrence.
      let nextOccurrence = candidateOpening.clone();
      let nextOccurrenceClosing = candidateClosing.clone();
      if (nextOccurrenceClosing.isBefore(now)) {
        nextOccurrence.add(7, "days");
      }

      nextCandidates.push(nextOccurrence.clone().utc());
    }
  }

  // Determine if currently online by checking if UTC now is in any period.
  let online = false;
  let currentOpeningTime = null;
  for (let period of periods) {
    if (nowUtc.isBetween(period.opening, period.closing, null, "[)")) {
      online = true;
      currentOpeningTime = period.original;
      break; // found an active period
    }
  }

  // Determine the next opening time from the candidates.
  let nextOpeningTime = null;
  const futureCandidates = nextCandidates.filter((candidate) =>
    candidate.isAfter(nowUtc)
  );
  if (futureCandidates.length > 0) {
    nextOpeningTime = futureCandidates.reduce((min, candidate) =>
      candidate.isBefore(min) ? candidate : min
    );
  } else {
    // In case none are in the future, fall back to the minimum candidate plus 7 days.
    nextOpeningTime = moment.min(nextCandidates).add(7, "days");
  }

  return {
    online,
    nextOpeningTime,
    currentOpeningTime,
  };
};
